import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { LineChart } from '@mui/x-charts';
import axiosInstance from '../../lib/axios';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

const EnquiryTypeChart = ({ propId, bookDtFrom, checkoutDt, refreshTrigger }) => {
    const [months, setMonths] = useState([]);
    const [seriesData, setSeriesData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchTypeChart();
        return () => {
            toast.dismiss();
        };
    }, [refreshTrigger]);

    const fetchTypeChart = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.post('BedCalendar/DashboardEnq?currentPage=1&limit=15', {
                PropId: propId || 2,
                BookDtFrom: bookDtFrom,
                CheckoutDt: checkoutDt,
                Flag: 'TotalEnqTypeMnth',
                start: 1,
                PageSize: 10,
                SearchText: '',
            });

            if (response.data.Status === 0 && response.data.responseStatusCode === 1 && response.data.Data?.length > 0) {
                processChartData(response.data.Data);
            } else {
                setMonths([]);
                setSeriesData([]);
            }
        } catch (error) {
            toast.error('Error while fetching the graph.');
            setMonths([]);
            setSeriesData([]);
        } finally {
            setLoading(false);
        }
    };

    const processChartData = (data) => {

        // Generate all months in the date range
        const startDate = dayjs(bookDtFrom);
        const endDate = dayjs(checkoutDt);
        const allMonths = [];
        let current = startDate;
        while (current.isBefore(endDate) || current.isSame(endDate, 'month')) {
            allMonths.push(current.format('MMMYY'));
            current = current.add(1, 'month');
        }

        // Get unique months from API data
        const apiMonths = [...new Set(data.map(item => item.MonthNm))];

        // Combine and sort months
        const uniqueMonths = [...new Set([...allMonths, ...apiMonths])].sort((a, b) => {
            const [monthA, yearA] = [a.slice(0, 3), a.slice(3)];
            const [monthB, yearB] = [b.slice(0, 3), b.slice(3)];
            const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            if (yearA === yearB) {
                return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
            }
            return parseInt(yearA) - parseInt(yearB);
        });

        // Define enquiry types
        const enquiryTypes = [
            { label: 'Hot', color: '#e53935' },
            { label: 'Warm', color: '#ffb300' },
            { label: 'Cold', color: '#1e88e5' },
        ];

        // Generate series data
        const series = enquiryTypes.map(type => {
            const dataForType = uniqueMonths.map(month => {
                const entry = data.find(item => item.MonthNm === month && item.Name.trim().toLowerCase() === type.label.toLowerCase());
                return entry ? entry.TotalCount : 0;
            });
            return {
                data: dataForType,
                label: type.label,
                color: type.color,
                area: true,
            };
        });

        setMonths(uniqueMonths);
        setSeriesData(series);
    };

    return (
        <Box>
            {loading ? (
                <Box sx={{ textAlign: 'center', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span>Loading...</span>
                </Box>
            ) : months.length > 0 && seriesData.length > 0 ? (
                <LineChart
                    width={600}
                    height={200}
                    series={seriesData}
                    xAxis={[{ scaleType: 'point', data: months }]}
                    sx={{
                        '.MuiLineElement-root': { strokeWidth: 1 },
                        '.MuiAreaElement-root': { fillOpacity: 0.2 },
                        '.MuiMarkElement-root': { display: 'none' },
                    }}
                />
            ) : (
                <Box sx={{ textAlign: 'center', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span>No data available</span>
                </Box>
            )}
        </Box>
    );
};

export default EnquiryTypeChart;